import React, { useState } from 'react';
import {
  DollarSign,
  Sliders,
  TrendingDown,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

export default function CostBreakdown({
  part_id = '',
  quantity: initialQty = 100,
  cycle_time_mins = 4.5,
  material = '',
  breakdown = {
    raw_material: 7.9,
    cnc_machining: 11.5,
    tooling_depreciation: 2.1,
    setup_and_overhead: 3.3
  },
  onQuantityChange
}) {
  const [qty, setQty] = useState(initialQty);
  const [expanded, setExpanded] = useState(true);

  // Amortization base model:
  // Fixed setup = $95.00
  // Variable per-unit = $23.85 (Material + Machining + Tooling + Direct Labor)
  const fixedSetup = 95.0;
  const variablePerUnit = 23.85;

  // Dynamic calculations based on slider
  const dynamicUnitCost = variablePerUnit + fixedSetup / qty;
  const dynamicTotalCost = dynamicUnitCost * qty;

  const handleSliderChange = (e) => {
    const val = parseInt(e.target.value, 10);
    setQty(val);
    if (onQuantityChange) onQuantityChange(val);
  };

  return (
    <div className="border border-slate-200 bg-white rounded-xl shadow-xs overflow-hidden my-3 text-slate-800 animate-in fade-in duration-200">
      {/* Header */}
      <div className="bg-slate-50/80 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-6 h-6 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs">
            <DollarSign className="w-3.5 h-3.5" />
          </div>
          <div>
            <h4 className="font-bold text-xs text-slate-900 tracking-tight flex items-center gap-2">
              <span>CNC Costing & Quotation Engine</span>
              {part_id && (
                <span className="font-mono text-[10px] text-slate-400 font-normal">
                  {part_id}
                </span>
              )}
            </h4>
            {material && (
              <p className="text-[11px] text-slate-500 mt-0.5">
                Alloy: <span className="font-semibold text-slate-700">{material}</span>
              </p>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 transition-colors cursor-pointer"
          title={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Primary KPI Metrics Row */}
      <div className="grid grid-cols-3 gap-2.5 p-3.5 bg-gradient-to-b from-slate-50/50 to-white border-b border-slate-100 text-center">
        {/* Unit Cost */}
        <div className="p-2.5 bg-blue-50/70 border border-blue-200/80 rounded-xl">
          <div className="text-[10px] font-semibold text-blue-700 uppercase tracking-wider">
            Cost Per Part
          </div>
          <div className="text-lg sm:text-xl font-extrabold text-blue-900 font-mono mt-0.5">
            ${dynamicUnitCost.toFixed(2)}
          </div>
          <div className="text-[9px] text-emerald-600 font-medium flex items-center justify-center gap-0.5 mt-0.5">
            <TrendingDown className="w-2.5 h-2.5" />
            <span>Volume amortized</span>
          </div>
        </div>

        {/* Total Batch Cost */}
        <div className="p-2.5 bg-indigo-50/70 border border-indigo-200/80 rounded-xl">
          <div className="text-[10px] font-semibold text-indigo-700 uppercase tracking-wider">
            Batch Total ({qty} pcs)
          </div>
          <div className="text-lg sm:text-xl font-extrabold text-indigo-900 font-mono mt-0.5">
            ${dynamicTotalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-[9px] text-slate-500 font-medium mt-0.5">
            Includes stock + tooling
          </div>
        </div>

        {/* Cycle Time */}
        <div className="p-2.5 bg-amber-50/70 border border-amber-200/80 rounded-xl">
          <div className="text-[10px] font-semibold text-amber-800 uppercase tracking-wider">
            Cycle Time
          </div>
          <div className="text-lg sm:text-xl font-extrabold text-amber-950 font-mono mt-0.5">
            {cycle_time_mins.toFixed(1)}m
          </div>
          <div className="text-[9px] text-amber-700 font-medium mt-0.5">
            Cycle amortized
          </div>
        </div>
      </div>

      {expanded && (
        <div className="p-3.5 space-y-3">
          {/* Interactive Batch Slider */}
          <div className="bg-slate-50/80 p-3 rounded-xl border border-slate-200/70 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-slate-800 flex items-center gap-1.5">
                <Sliders className="w-3.5 h-3.5 text-blue-600" />
                <span>Simulate Order Quantity</span>
              </span>
              <span className="font-mono font-bold text-blue-700 text-xs px-2 py-0.5 rounded bg-blue-100/70">
                {qty} Units
              </span>
            </div>

            <input
              type="range"
              min="1"
              max="500"
              value={qty}
              onChange={handleSliderChange}
              className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />

            <div className="flex justify-between text-[10px] text-slate-400 font-mono">
              <button
                type="button"
                onClick={() => setQty(1)}
                className="hover:text-blue-600 cursor-pointer"
              >
                1 (Prototype)
              </button>
              <button
                type="button"
                onClick={() => setQty(50)}
                className="hover:text-blue-600 cursor-pointer"
              >
                50
              </button>
              <button
                type="button"
                onClick={() => setQty(100)}
                className="hover:text-blue-600 cursor-pointer"
              >
                100 (Pilot)
              </button>
              <button
                type="button"
                onClick={() => setQty(250)}
                className="hover:text-blue-600 cursor-pointer"
              >
                250
              </button>
              <button
                type="button"
                onClick={() => setQty(500)}
                className="hover:text-blue-600 cursor-pointer"
              >
                500 (Production)
              </button>
            </div>
          </div>

          {/* Cost Drivers Breakdown */}
          {breakdown && Object.keys(breakdown).length > 0 && (
            <div className="space-y-1.5 pt-1">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Manufacturing Cost Drivers ($/part)
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {Object.entries(breakdown).map(([k, v]) => (
                  <div
                    key={k}
                    className="p-2 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-between"
                  >
                    <span className="capitalize text-slate-600 text-[11px]">
                      {k.replace(/_/g, ' ')}
                    </span>
                    <span className="font-mono font-bold text-slate-900 text-xs">
                      ${typeof v === 'number' ? v.toFixed(2) : v}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
